import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  CreateCommentData,
  DocumentComment,
  DocumentReview,
  RequestReviewData
} from "../types/project-attachment-workflow.types.ts"

const REVIEWS_URL = "/project-management/project-attachment-reviews"
const COMMENTS_URL = "/project-management/project-attachment-comments"

const PATHS = {
  reviews: `${REVIEWS_URL}/`,
  review: (id: string) => `${REVIEWS_URL}/${id}/`,
  approve: (id: string) => `${REVIEWS_URL}/${id}/approve/`,
  reject: (id: string) => `${REVIEWS_URL}/${id}/reject/`,
  requestChanges: (id: string) => `${REVIEWS_URL}/${id}/request-changes/`,
  comments: `${COMMENTS_URL}/`,
  comment: (id: string) => `${COMMENTS_URL}/${id}/`,
  resolve: (id: string) => `${COMMENTS_URL}/${id}/resolve/`
}

// GET /project-management/project-attachment-reviews/?attachment=:id — every
// review across the whole version chain, not only this exact row (bare GET).
export const getDocumentReviews = async (attachmentId: string) => {
  return request.api.get<BasePagination<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.reviews,
    { attachment: attachmentId }
  )
}

// POST /project-management/project-attachment-reviews/ — request review.
export const requestDocumentReview = async (data: RequestReviewData) => {
  return request.api.post<BaseApiResponse<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.reviews,
    data
  )
}

// POST /project-management/project-attachment-reviews/:id/approve/
export const approveDocumentReview = async (id: string) => {
  return request.api.post<BaseApiResponse<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.approve(id)
  )
}

// POST /project-management/project-attachment-reviews/:id/reject/ — requires a reason.
export const rejectDocumentReview = async (id: string, reason: string) => {
  return request.api.post<BaseApiResponse<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.reject(id),
    { reason }
  )
}

// POST /project-management/project-attachment-reviews/:id/request-changes/ —
// sends the document back for revision (distinct from Reject); requires a reason.
export const requestChangesOnReview = async (id: string, reason: string) => {
  return request.api.post<BaseApiResponse<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.requestChanges(id),
    { reason }
  )
}

// GET /project-management/project-attachment-reviews/?reviewer=me&status=pending
// — every document across every project waiting on the caller's review. Backs
// the Approval nav's "My Approvals" view.
export const getMyPendingReviews = async () => {
  return request.api.get<BasePagination<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.reviews,
    { reviewer: "me", status: "pending" }
  )
}

// GET /project-management/project-attachment-reviews/?requested_by=me —
// every review the caller has sent, across every project. Backs the
// Approval nav's "Sent for Review" view.
export const getReviewsSentByMe = async () => {
  return request.api.get<BasePagination<DocumentReview>, BaseApiResponse<unknown>>(
    PATHS.reviews,
    { requested_by: "me" }
  )
}

// GET /project-management/project-attachment-comments/?attachment=:id —
// top-level comments (with nested replies) across the whole version chain.
export const getDocumentComments = async (attachmentId: string) => {
  return request.api.get<BasePagination<DocumentComment>, BaseApiResponse<unknown>>(
    PATHS.comments,
    { attachment: attachmentId }
  )
}

// POST /project-management/project-attachment-comments/
export const createDocumentComment = async (data: CreateCommentData) => {
  return request.api.post<BaseApiResponse<DocumentComment>, BaseApiResponse<unknown>>(
    PATHS.comments,
    data
  )
}

// POST /project-management/project-attachment-comments/:id/resolve/
export const resolveDocumentComment = async (id: string) => {
  return request.api.post<BaseApiResponse<DocumentComment>, BaseApiResponse<unknown>>(
    PATHS.resolve(id)
  )
}
